/**
 * Admin Ambassador Application Detail Page
 *
 * Detailed view for reviewing a pending ambassador application.
 * Shows wallet analysis, collections held, multi-wallet detection, and approve/reject actions.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  getAdminApplicationDetail,
  approveApplication,
  rejectApplication,
} from '../api/ambassadors';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatAddr(addr: string): string {
  if (!addr || addr.length < 12) return addr || '-';
  return `${addr.slice(0, 8)}...${addr.slice(-4)}`;
}

function formatHoldTime(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '-';
  const days = Math.floor(seconds / 86400);
  if (days > 365) return `${(days / 365).toFixed(1)} years`;
  if (days > 30) return `${Math.floor(days / 30)} months`;
  return `${days} days`;
}

export function AdminAmbassadorApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getAdminApplicationDetail(id);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load application detail:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleApprove = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to approve this application? The user will be notified and added as an active ambassador.')) return;
    setActionLoading(true);
    try {
      await approveApplication(id);
      navigate('/admin/ambassadors?tab=applications');
    } catch (err: any) {
      alert(err.message || 'Failed to approve application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    const reason = prompt('Reason for rejection (will not be shown to the applicant):');
    if (!reason) return;
    setActionLoading(true);
    try {
      await rejectApplication(id, reason);
      navigate('/admin/ambassadors?tab=applications');
    } catch (err: any) {
      alert(err.message || 'Failed to reject application');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <AdminLayout><div className="py-8 text-center text-gray-500">Loading...</div></AdminLayout>;
  }

  if (!data) {
    return <AdminLayout><div className="py-8 text-center text-red-600">Application not found</div></AdminLayout>;
  }

  const wallet = data.wallet;
  const isPending = data.status === 'pending';

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Back link */}
        <button
          onClick={() => navigate('/admin/ambassadors?tab=applications')}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          &larr; Back to Applications
        </button>

        {/* Header */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {data.username || formatAddr(data.wallet_address)}
              </h2>
              <p className="text-sm text-gray-500">{data.wallet_address}</p>
              {data.email && <p className="text-sm text-gray-500">{data.email}</p>}
              {data.x_account && (
                <a
                  href={`https://x.com/${data.x_account}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  @{data.x_account}
                </a>
              )}
              <div className="mt-2 flex items-center gap-4">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  data.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  data.status === 'active' ? 'bg-green-100 text-green-800' :
                  data.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
                </span>
                {data.applied_at && <span className="text-sm text-gray-500">Applied {formatDate(data.applied_at)}</span>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={`/wallets/${data.wallet_address}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                View Wallet
              </a>
              <a
                href={`/inbox?start=${data.wallet_address}`}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                Message
              </a>
            </div>
          </div>
        </div>

        {/* Multi-wallet warning */}
        {data.linkedAmbassadors && data.linkedAmbassadors.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
            <h3 className="text-sm font-semibold text-amber-800 mb-2">
              This user has other wallets in the ambassador program
            </h3>
            <div className="space-y-2">
              {data.linkedAmbassadors.map((linked: any) => (
                <div key={linked.id} className="flex items-center justify-between bg-white rounded-md border border-amber-200 px-3 py-2 text-sm">
                  <div>
                    <a
                      href={`/admin/ambassadors/members/${linked.id}`}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {formatAddr(linked.wallet_address)}
                    </a>
                    <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      linked.status === 'active' ? 'bg-green-100 text-green-800' :
                      linked.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {linked.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Score: <strong className="text-gray-900">{Number(linked.score).toFixed(1)}</strong></span>
                    <span>Free mints: <strong className="text-gray-900">{linked.total_free_mints}</strong></span>
                    <span>Violations: <strong className={linked.violations_count > 0 ? 'text-red-600' : 'text-gray-900'}>{linked.violations_count}</strong></span>
                    {linked.joined_at && <span>Joined {formatDate(linked.joined_at)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Linked wallets */}
        {data.linkedWallets && data.linkedWallets.length > 1 && (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Linked Wallets ({data.linkedWallets.length})</h3>
            <div className="flex flex-wrap gap-2">
              {data.linkedWallets.map((w: any) => (
                <a
                  key={w.xrpl_address}
                  href={`/wallets/${w.xrpl_address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                    w.xrpl_address === data.wallet_address ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {formatAddr(w.xrpl_address)}
                  {w.is_primary && <span className="text-[10px] text-gray-400">(primary)</span>}
                  {w.xrpl_address === data.wallet_address && <span className="text-[10px] text-blue-500">(applying)</span>}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Wallet Analysis */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Wallet Analysis</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.xrpBalance != null && (
              <div>
                <div className="text-xs text-gray-500">XRP Balance</div>
                <div className="text-lg font-semibold">{data.xrpBalance.toFixed(2)} XRP</div>
              </div>
            )}
            <div>
              <div className="text-xs text-gray-500">NFTs Owned</div>
              <div className="text-lg font-semibold">{data.onLedgerNftCount ?? wallet?.total_nfts_held ?? 0}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Collections</div>
              <div className="text-lg font-semibold">{data.onLedgerCollectionCount ?? '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Total Buys</div>
              <div className="text-lg font-semibold">{wallet?.total_buys ?? 0}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Total Sells</div>
              <div className="text-lg font-semibold">{wallet?.total_sells ?? 0}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Total Transfers</div>
              <div className="text-lg font-semibold">{wallet?.total_transfers ?? 0}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Avg Hold (Sold)</div>
              <div className="text-lg font-semibold">{formatHoldTime(wallet?.avg_hold_time_sold_seconds)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Avg Hold (Current)</div>
              <div className="text-lg font-semibold">{formatHoldTime(wallet?.avg_hold_time_current_seconds)}</div>
            </div>
            {wallet?.total_profit != null && (
              <div>
                <div className="text-xs text-gray-500">Realized P&L</div>
                <div className={`text-lg font-semibold ${parseFloat(wallet.total_profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {parseFloat(wallet.total_profit).toFixed(1)} XRP
                </div>
              </div>
            )}
            {wallet?.roi != null && (
              <div>
                <div className="text-xs text-gray-500">ROI</div>
                <div className={`text-lg font-semibold ${parseFloat(wallet.roi) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {parseFloat(wallet.roi).toFixed(1)}%
                </div>
              </div>
            )}
          </div>

          {/* Mint volume & DEALS balance */}
          {(data.mintVolume || data.dealsBalance !== null) && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-6">
                {data.mintVolume && (
                  <>
                    <div>
                      <div className="text-xs text-gray-500">Mint Volume</div>
                      <div className="text-lg font-semibold">{data.mintVolume.totalXrp.toFixed(1)} XRP</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Total Mints</div>
                      <div className="text-lg font-semibold">{data.mintVolume.mintCount}</div>
                    </div>
                  </>
                )}
                {data.dealsBalance !== null && data.dealsBalance !== undefined && (
                  <div>
                    <div className="text-xs text-gray-500"><a href="https://firstledger.net/token-v2/rfnKKs998kvuVvJSW4jZ1MDxMSiCjhs9su/4445414C53000000000000000000000000000000" target="_blank" rel="noopener noreferrer" className="hover:underline">$DEALS</a> Balance</div>
                    <div className="text-lg font-semibold">{Math.round(data.dealsBalance).toLocaleString()}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Collections Held */}
        {data.collections && data.collections.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Collections Held (indexed)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Collection</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">NFTs</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">Floor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {data.collections.map((col: any) => (
                    <tr key={col.slug}>
                      <td className="px-4 py-2 text-sm">
                        <a href={`/wallets/${data.wallet_address}/${col.collection_id}?tab=nfts`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          {col.name}
                        </a>
                      </td>
                      <td className="px-4 py-2 text-sm text-right">{col.nfts_held}</td>
                      <td className="px-4 py-2 text-sm text-right text-gray-500">
                        {col.floor_price && parseFloat(col.floor_price) > 0 ? `${parseFloat(col.floor_price).toFixed(1)} XRP` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Launches */}
        {data.launches && data.launches.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Launches Created</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">Minted</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {data.launches.map((launch: any) => (
                    <tr key={launch.id}>
                      <td className="px-4 py-2 text-sm">
                        {launch.slug ? (
                          <a href={`/drops/${launch.slug}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                            {launch.name}
                          </a>
                        ) : launch.name}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          launch.status === 'completed' ? 'bg-green-100 text-green-800' :
                          launch.status === 'active' ? 'bg-blue-100 text-blue-800' :
                          launch.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {launch.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-right">
                        {launch.minted_count ?? 0}{launch.total_supply ? ` / ${launch.total_supply}` : ''}
                      </td>
                      <td className="px-4 py-2 text-sm text-right text-gray-500">
                        {launch.completed_at ? formatDate(launch.completed_at) :
                         launch.launched_at ? formatDate(launch.launched_at) :
                         formatDate(launch.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Actions */}
        {isPending && (
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Review Decision</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Approve Application'}
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Reject Application
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
